import * as React from 'react';
import styled from 'styled-components/macro';
import { ErrorMessage, Field, FieldProps, useFormikContext } from 'formik';
import { EmailInput, TextInput } from 'lib/components';
import { useAuth } from 'lib/context';
import { theme } from '../../../../../../lib/style';
import { checkUsername } from 'lib/api';
import debounce from 'lodash/debounce';
import selectors from '../../../../../../cypress/selectors';
import { NAPLETON_AUTOMOTIVE_RESELLER_ID } from 'lib/const/SuperAdminConstants';
import { AccessRole } from 'lib/const';
import { useCDK } from 'lib/hooks/useCdk';
import { FormikDropdownField } from 'lib/components/formik/FormikDropdown';
import { Flex, FlexProps } from 'lib/components/styles/layout';
import { useGetStaticDropdownOptions } from 'lib/hooks/useGetStaticDropdownOptions';
import { useTranslation } from 'react-i18next';
import { UserValues } from './userValidator';

// Added (userId) in FormProps interface for SUS-521

interface FormProps {
  departmentsOptions: { value: string; label: string }[];
  deptLoading?: any;
  userId?: string;
  setUsernameValid: React.Dispatch<React.SetStateAction<boolean>>;
  isUsernameValid: boolean;
  hasEmail2Field?: boolean;
}

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: flex-start;
  width: 100%;
  padding: 0.5rem 0;
  .user__desc {
    font-size: 12px;
    line-height: 20px;
  }
  @media screen and (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FieldContainer = styled.section`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const ErrorMsg = styled.p`
  color: red;
  font-size: 14px;
  padding: 0.1rem;
`;

const FieldLabel = styled.label`
  width: 120px;
  margin-right: 8px;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.palette.gray60};
`;

const SectionTitle = styled.p`
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  margin: 0;
  width: 100%;
  max-width: 200px;
  line-height: 24px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  max-width: 700px;
  margin-left: 0;
  margin-top: 25px;
  margin-bottom: 25px;
  background-color: #eeeff2;
`;

const RowWrapper = styled.div`
  width: 100%;
  div:first-child,
  p:first-child {
    padding-top: 0px;
  }
`;

export const UserForm = (props: FormProps) => {
  const {
    departmentsOptions,
    deptLoading,
    setUsernameValid,
    isUsernameValid,
    hasEmail2Field,
  } = props;

  const { t } = useTranslation();
  const { isSubmitting } = useFormikContext<UserValues>();

  const extendStyles = {
    labelStyles: {
      width: '120px',
      maxHeight: '40px',
    },
    mainWrapper: { flexDirection: 'row' },
  } as {
    labelStyles: React.CSSProperties;
    mainWrapper: FlexProps;
  };

  const { languageOptions } = useGetStaticDropdownOptions();
  const usernameRef = React.useRef<HTMLInputElement | null>(null);
  const handleDebounceFn = async () => {
    const trimmedUserName = usernameRef?.current?.value.trim();
    if (trimmedUserName !== '') {
      // Added argument for SUS-521
      const userValid = await checkUsername(
        trimmedUserName ? trimmedUserName : '',
        props.userId ? props.userId : ''
      );
      setUsernameValid(!userValid.userExist);
    } else {
      setUsernameValid(true);
    }
  };
  const debounceFn = React.useMemo(() => debounce(handleDebounceFn, 500), []);
  const { isCdkUser } = useCDK();
  const {
    userData: { eleadEnabled, isAutomotive, customer, access, customerId },
  } = useAuth();

  const customerIdsForReadOnlyPhone = [66766, 66765];
  const isReadOnlyPhone =
    customerIdsForReadOnlyPhone.includes(
      parseInt(customerId?.toString(), 10)
    ) && access === AccessRole.USER;

  return (
    <>
      {/* Personal info */}
      <Flex flexDirection='row' maxWidth='700px'>
        <SectionTitle>{t('Personal info')}</SectionTitle>
        <RowWrapper>
          {/* Sign Up Date */}
          <Row>
            <FieldLabel>{t('Sign Up Date')}</FieldLabel>
            <Field name='signupDate'>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput
                      {...field}
                      type='text'
                      style={{ backgroundColor: theme.palette.gray10 }}
                      placeholder={t('Sign Up Date')}
                      required={true}
                      disabled={true}
                    />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>

          {/* First Name */}
          <Row>
            <FieldLabel>{t('First Name')}</FieldLabel>
            <Field name='firstName'>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput
                      {...field}
                      type='text'
                      placeholder={t('First Name')}
                      required={true}
                      disabled={isSubmitting}
                      dataCy={selectors.editUserProfile.firstNameField}
                    />
                    <ErrorMessage name='firstName' component={ErrorMsg} />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>

          {/* Last Name */}
          <Row>
            <FieldLabel>{t('Last Name')}</FieldLabel>
            <Field name='lastName'>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput
                      {...field}
                      type='text'
                      placeholder={t('Last Name')}
                      required={
                        Number(customer.resellerId) ===
                        NAPLETON_AUTOMOTIVE_RESELLER_ID
                          ? false
                          : true
                      }
                      disabled={isSubmitting}
                      dataCy={selectors.editUserProfile.lastNameField}
                    />
                    <ErrorMessage name='lastName' component={ErrorMsg} />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>

          {/* Email */}
          <Row>
            <FieldLabel>{t('Email')}</FieldLabel>
            <Field name='email'>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <EmailInput
                      {...field}
                      type='email'
                      required={true}
                      disabled={isSubmitting || isCdkUser}
                      data-cy={selectors.editUserProfile.emailField}
                    />
                    <ErrorMessage name='email' component={ErrorMsg} />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>
          {hasEmail2Field && (
            <Row>
              <FieldLabel>{t('Email 2')}</FieldLabel>
              <Field name='email2'>
                {({ field }: FieldProps) => {
                  return (
                    <FieldContainer>
                      <EmailInput
                        {...field}
                        type='email'
                        required={false}
                        disabled={isSubmitting}
                        data-cy={selectors.editUserProfile.emailField2}
                      />
                      <ErrorMessage name='email2' component={ErrorMsg} />
                    </FieldContainer>
                  );
                }}
              </Field>
            </Row>
          )}

          {/* Username */}
          <Row>
            <FieldLabel>{t('Username')}</FieldLabel>
            <Field name='username'>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput
                      {...field}
                      type='text'
                      placeholder={t('Username')}
                      autoComplete='new-password'
                      required={true}
                      disabled={isSubmitting || isCdkUser}
                      ref={usernameRef}
                      dataCy={selectors.editUserProfile.userNameField}
                      onKeyUp={() => {
                        debounceFn();
                      }}
                    />
                    <ErrorMessage name='username' component={ErrorMsg} />
                    {isUsernameValid && (
                      <p className='user__desc'>
                        {t(
                          'Username should only contain letters, numbers, and dots without spaces, only @-_+. special characters can be used.'
                        )}
                      </p> // Updated message as per SUS-784 changes
                    )}
                    {!isUsernameValid && (
                      <ErrorMsg>{t('Username is already taken.')}</ErrorMsg>
                    )}
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>
        </RowWrapper>
      </Flex>
      <Divider />

      {/* Contact */}
      <Flex flexDirection='row' maxWidth='700px'>
        <SectionTitle>{t('Contact')}</SectionTitle>
        <RowWrapper>
          {/* Phone 1 */}
          <Row>
            <FieldLabel>{t('Phone 1')}</FieldLabel>
            <Field name='phone1'>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput
                      {...field}
                      type='text'
                      required={false}
                      readOnly={isReadOnlyPhone}
                      disabled={isSubmitting || isReadOnlyPhone}
                    />
                    <ErrorMessage name='phone1' component={ErrorMsg} />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>

          {/* Phone 2 */}
          <Row>
            <FieldLabel>{t('Phone 2')}</FieldLabel>
            <Field name='phone2'>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput
                      {...field}
                      type='text'
                      disabled={isSubmitting || isReadOnlyPhone}
                      readOnly={isReadOnlyPhone}
                    />
                    <ErrorMessage name='phone2' component={ErrorMsg} />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>

          {/* Cell Phone */}
          <Row>
            <FieldLabel>{t('Cell Phone')}</FieldLabel>
            <Field name='cellPhone'>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput
                      {...field}
                      type='text'
                      disabled={isSubmitting || isReadOnlyPhone}
                      readOnly={isReadOnlyPhone}
                    />
                    <ErrorMessage name='cellPhone' component={ErrorMsg} />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>

          {/* Website */}
          <Row>
            <FieldLabel>{t('Website')}</FieldLabel>
            <Field name='website'>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput {...field} type='text' disabled={isSubmitting} />
                    <ErrorMessage name='website' component={ErrorMsg} />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>
        </RowWrapper>
      </Flex>
      <Divider />

      {/* Job position */}
      <Flex flexDirection='row' maxWidth='700px'>
        <SectionTitle>{t('Job position')}</SectionTitle>
        <RowWrapper>
          {/* Title */}
          <Row>
            <FieldLabel>{t('Title')}</FieldLabel>
            <Field name='userTitle'>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput {...field} type='text' disabled={isSubmitting} />
                    <ErrorMessage name='userTitle' component={ErrorMsg} />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>

          {/* Department */}
          <Row>
            <FormikDropdownField
              extendStyles={extendStyles}
              name='department'
              options={[
                { label: t('None'), value: null },
                ...departmentsOptions,
              ]}
              placeholder={t('Select Department')}
              isLoading={deptLoading}
              label={t('Department')}
              isSearchable={false}
              isClearable={false}
              disabled={isSubmitting}
            />
          </Row>

          {/* NMLS */}
          {!isAutomotive && (
            <Row>
              <FieldLabel>{t('NMLS')}</FieldLabel>
              <Field name='nmls'>
                {({ field }: FieldProps) => {
                  return (
                    <FieldContainer>
                      <TextInput
                        {...field}
                        type='text'
                        disabled={isSubmitting}
                      />
                      <ErrorMessage name='nmls' component={ErrorMsg} />
                    </FieldContainer>
                  );
                }}
              </Field>
            </Row>
          )}

          {/* eleadEnabled?.toString() to show eleadField if it is enabled SUS-646 */}
          {eleadEnabled?.toString() === '1' && (
            <>
              {/* Elead DMS Employee ID */}
              <Row>
                <FieldLabel>{t('Elead DMS Employee ID')}</FieldLabel>
                <Field name='eleadEmployeeId'>
                  {({ field }: FieldProps) => {
                    return (
                      <FieldContainer>
                        <TextInput
                          {...field}
                          type='text'
                          placeholder={t('Elead DMS Employee ID')}
                          required={true} // changing this to true in case user has enabled the Elead settings SUS-646
                          disabled={isSubmitting}
                        />
                        <ErrorMessage
                          name='eleadEmployeeId'
                          component={ErrorMsg}
                        />
                      </FieldContainer>
                    );
                  }}
                </Field>
              </Row>
            </>
          )}
        </RowWrapper>
      </Flex>
      <Divider />

      <Flex flexDirection='row' maxWidth='700px'>
        <SectionTitle>{t('Preffered langauge')}</SectionTitle>
        <RowWrapper>
          <Row>
            <FormikDropdownField
              extendStyles={extendStyles}
              name='language'
              options={languageOptions}
              placeholder={t('Select Language')}
              label={t('Language')}
              formatOptionLabel={option => (
                <div
                  style={{
                    display: 'flex',
                    gap: 4,
                    alignItems: 'center',
                  }}
                >
                  {option.label}
                </div>
              )}
              isSearchable={false}
              isClearable={false}
              disabled={isSubmitting}
            />
          </Row>
        </RowWrapper>
      </Flex>
    </>
  );
};
